import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { collectionGroup, onSnapshot } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { ArrowLeft, ChevronLeft, ChevronRight, Clock, MapPin, Printer, ShoppingBag, ArrowUp } from 'lucide-react';
import { WeeklyOrder, OrderItem } from './delivery_start';
import lang from '../language/Eng_Aust.json';
import { getNextMondayDateString, getCurrentMondayDateString, getWeekDays, formatDate } from './utils';

const PageContainer = styled.div`
  min-height: 100vh;
  background-color: #FDFBF9;
  padding: 40px 20px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  display: flex;
  justify-content: center;

  @media print {
    background-color: white;
    padding: 0;
    .no-print {
      display: none !important;
    }
  }
`;

const ContentWrapper = styled.div`
  width: 100%;
  max-width: 1200px;

  @media (max-width: 768px) {
    max-width: 600px;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 32px;

  @media print {
    display: none !important;
  }
`;

const BackButton = styled.button`
  background: white;
  border: 1px solid #EAEAEA;
  border-radius: 12px;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #333;
  transition: all 0.2s ease;

  &:hover {
    background: #F5F5F5;
    transform: translateY(-2px);
  }
`;

const Title = styled.h1`
  font-size: 28px;
  color: #1A1A1A;
  margin: 0;
  font-weight: 800;
  letter-spacing: -0.5px;
`;

const LogoutButton = styled.button`
  margin-top: 40px;
  background: none;
  border: none;
  color: #666;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  text-decoration: underline;
  transition: color 0.2s;
  padding: 16px;

  &:hover {
    color: #333;
  }
`;

const ScrollToTopButton = styled.button<{ $visible: boolean }>`
  position: fixed;
  bottom: 30px;
  right: 30px;
  width: 50px;
  height: 50px;
  border-radius: 25px;
  background-color: #1A1A1A;
  color: white;
  border: none;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  opacity: ${props => props.$visible ? 1 : 0};
  visibility: ${props => props.$visible ? 'visible' : 'hidden'};
  transform: translateY(${props => props.$visible ? '0' : '20px'});
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 1000;

  &:hover {
    background-color: #333;
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
  }
  
  @media (max-width: 768px) {
    bottom: 20px;
    right: 20px;
    width: 44px;
    height: 44px;
  }
`;

const OrderCard = styled.div`
  background: white;
  border-radius: 20px;
  padding: 24px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.03);
  border: 1px solid rgba(0,0,0,0.05);
  margin-bottom: 16px;

  @media print {
    page-break-inside: avoid;
    box-shadow: none;
    border: 1px solid #ccc;
    margin-bottom: 20px;
  }
`;

const OrderHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid #F4F4F4;
`;

const OrderMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const InfoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 15px;
  color: #1A1A1A;
  font-weight: 600;
`;

const ItemsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ItemRow = styled.div`
  display: flex;
  align-items: center;
  font-size: 15px;
  color: #444;
`;

const ItemBadge = styled.span`
  background-color: #FFF2D1;
  color: #937A3B;
  padding: 4px 8px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 700;
  margin-right: 12px;
  min-width: 32px;
  text-align: center;
`;

const PrintCheckbox = styled.div`
  display: none;
  width: 16px;
  height: 16px;
  border: 1.5px solid #000;
  border-radius: 3px;
  flex-shrink: 0;
  margin-right: 10px;

  @media print {
    display: inline-block;
  }
`;

const PrintButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: white;
  border: 1px solid #EAEAEA;
  border-radius: 12px;
  padding: 12px 20px;
  font-size: 15px;
  font-weight: 600;
  color: #333;
  cursor: pointer;
  transition: background-color 0.2s;
  margin: 0 auto 16px;

  &:hover {
    background-color: #F5F5F5;
  }
`;

const OrdersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }

  @media print {
    display: none !important;
  }
`;

const PrintBranchCard = styled.div`
  display: none;

  @media print {
    display: block;
    page-break-after: always;
    page-break-inside: avoid;
    padding: 20px 24px;
    font-family: 'Inter', Arial, sans-serif;
  }

  &:last-child {
    @media print {
      page-break-after: auto;
    }
  }
`;

const PrintBranchHeader = styled.div`
  display: none;

  @media print {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    border-bottom: 3px solid #000;
    padding-bottom: 10px;
    margin-bottom: 18px;
  }
`;

const PrintBranchName = styled.h2`
  display: none;

  @media print {
    display: block;
    font-size: 20pt;
    font-weight: 900;
    margin: 0;
    color: #000;
    letter-spacing: -0.5px;
  }
`;

const PrintWeekLabel = styled.span`
  display: none;

  @media print {
    display: block;
    font-size: 11pt;
    color: #444;
    font-weight: 500;
  }
`;

const PrintDayBlock = styled.div`
  display: none;

  @media print {
    display: block;
    margin-bottom: 14px;
    break-inside: avoid;
  }
`;

const PrintDayHeader = styled.div`
  display: none;

  @media print {
    display: block;
    font-size: 11pt;
    font-weight: 800;
    color: #000;
    background: #f0f0f0;
    padding: 4px 8px;
    margin-bottom: 6px;
    border-radius: 2px;
  }
`;

const PrintItemLine = styled.div`
  display: none;

  @media print {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 4px 8px;
    font-size: 10.5pt;
    line-height: 1.5;
    border-bottom: 1px solid #e8e8e8;
    color: #111;
  }

  &:last-child {
    @media print {
      border-bottom: none;
    }
  }
`;

const PrintCheckboxPrint = styled.span`
  display: none;

  @media print {
    display: inline-block;
    width: 14px;
    height: 14px;
    border: 1.5px solid #333;
    border-radius: 2px;
    flex-shrink: 0;
    margin-right: 2px;
  }
`;

const PrintQty = styled.span`
  display: none;

  @media print {
    display: inline-block;
    font-weight: 700;
    font-size: 10.5pt;
    min-width: 28px;
    color: #000;
  }
`;

const PrintItemName = styled.span`
  display: none;

  @media print {
    display: inline;
    font-size: 10.5pt;
    color: #111;
  }
`;

const TopActions = styled.div`
  display: none;
  gap: 12px;
  margin-bottom: 20px;

  @media (max-width: 900px) {
    display: flex;
    justify-content: center;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 0;
  background: white;
  border-radius: 20px;
  border: 1px dashed #EAEAEA;
  margin-bottom: 24px;
`;

const WeekNavContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin: 16px 0 24px 0;

  @media print {
    display: none !important;
  }
`;

const NavButton = styled.button`
  background: white;
  border: 1px solid #EAEAEA;
  border-radius: 8px;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #333;
  transition: background-color 0.2s;

  &:hover {
    background-color: #F5F5F5;
  }
`;

const WeekLabel = styled.div`
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: #1A1A1A;
  padding: 8px 16px;
  border: 1px solid #EAEAEA;
  border-radius: 8px;
  text-align: center;
  background: white;
`;

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const Spinner = styled.div`
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-left-color: #FCE17A;
  border-radius: 50%;
  width: 48px;
  height: 48px;
  animation: ${spin} 1s linear infinite;
  margin-bottom: 16px;
`;

const DaySection = styled.div`
  margin-bottom: 24px;
`;

const DayTitle = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: #937A3B;
  border-bottom: 1px solid #FFF2D1;
  padding-bottom: 6px;
  margin-bottom: 12px;
  
  @media print {
    color: #000;
    border-bottom: 1px solid #000;
  }
`;

const DeliveryList: React.FC = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<WeeklyOrder[]>([]);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [currentUserEmail, setCurrentUserEmail] = useState<string | null>(null);
  const [showScrollTop, setShowScrollTop] = useState(false);

  const [selectedWeek, setSelectedWeek] = useState<string>(getNextMondayDateString());
  const weekDays = getWeekDays(selectedWeek);

  const handlePrevWeek = () => {
    const [y, m, d] = selectedWeek.split('-').map(Number);
    const date = new Date(y, m - 1, d);
    date.setDate(date.getDate() - 7);
    setSelectedWeek(formatDate(date));
  };

  const handleNextWeek = () => {
    const [y, m, d] = selectedWeek.split('-').map(Number);
    const date = new Date(y, m - 1, d);
    date.setDate(date.getDate() + 7);
    setSelectedWeek(formatDate(date));
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) {
        navigate('/delivery/login');
      } else {
        setCurrentUserEmail(user.email);
        setIsCheckingAuth(false);
      }
    });

    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };
    window.addEventListener('scroll', handleScroll);

    return () => {
      unsubscribe();
      window.removeEventListener('scroll', handleScroll);
    };
  }, [navigate]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/delivery/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  useEffect(() => {
    if (isCheckingAuth || !currentUserEmail) return;

    setIsLoadingData(true);
    const branchesRef = collectionGroup(db, 'branches');
    // Keep open order pages in sync with edits saved by any branch.
    return onSnapshot(branchesRef, (querySnapshot) => {
      let fetchedOrders: WeeklyOrder[] = [];
      querySnapshot.forEach((doc) => {
        fetchedOrders.push({ id: doc.id, ...doc.data() } as WeeklyOrder);
      });

      fetchedOrders = fetchedOrders.filter(o => o.weekStartDate === selectedWeek);

      if (currentUserEmail.toLowerCase() !== 'butkitchen@butteredsydney.com.au') {
        let branchId = 'unknown';
        if (currentUserEmail.toLowerCase() === 'hornsbymanager@firestone-sydney.com.au') {
          branchId = 'firestone-hornsby';
        } else if (currentUserEmail.toLowerCase() === 'chatswood@vuzahospitality.com.au') {
          branchId = 'chatswood';
        } else if (currentUserEmail.toLowerCase() === 'firestoneeastwood@firestone-sydney.com.au') {
          branchId = 'firestone-eastwood';
        }
        fetchedOrders = fetchedOrders.filter(o => o.branchId === branchId);
      }

      fetchedOrders.sort((a, b) => {
        const timeA = a.createdAt?.toMillis ? a.createdAt.toMillis() : 0;
        const timeB = b.createdAt?.toMillis ? b.createdAt.toMillis() : 0;
        return timeB - timeA;
      });

      setOrders(fetchedOrders);
      setIsLoadingData(false);
    }, (error) => {
      console.error('Error subscribing to orders:', error);
      setIsLoadingData(false);
    });
  }, [isCheckingAuth, currentUserEmail, selectedWeek]);

  if (isCheckingAuth || isLoadingData) {
    return (
      <PageContainer style={{ justifyContent: 'center', alignItems: 'center', display: 'flex', flexDirection: 'column' }}>
        <Spinner />
        <h2 style={{ color: '#666', fontSize: '18px', fontWeight: 500 }}>{lang.list.loading}</h2>
      </PageContainer>
    );
  }

  const renderOrderDetails = (order: WeeklyOrder) => {
    return ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(dayName => {
      const dayData = weekDays.find(d => d.dayName === dayName);
      const items = order.dailyItems?.[dayName] || [];
      
      if (items.length === 0) return null;

      return (
        <DaySection key={dayName}>
          <DayTitle>{dayName} ({dayData?.shortDate})</DayTitle>
          <ItemsList>
            {items.map((item: OrderItem) => (
              <ItemRow key={item.id}>
                <PrintCheckbox />
                <ItemBadge>{item.quantity}x</ItemBadge>
                <span>{item.name}</span>
              </ItemRow>
            ))}
          </ItemsList>
        </DaySection>
      );
    });
  };

  return (
    <PageContainer>
      <ContentWrapper>
        <Header>
          <BackButton onClick={() => navigate('/delivery/main')}>
            <ArrowLeft size={20} />
          </BackButton>
          <Title>Weekly Order History</Title>
        </Header>

        <WeekNavContainer>
          <NavButton onClick={handlePrevWeek}>
            <ChevronLeft size={20} />
          </NavButton>
          <WeekLabel>
            Week of {weekDays[0].shortDate} - {weekDays[6].shortDate}
          </WeekLabel>
          <NavButton onClick={handleNextWeek}>
            <ChevronRight size={20} />
          </NavButton>
        </WeekNavContainer>

        {orders.length === 0 ? (
          <EmptyState>
            <ShoppingBag size={48} color="#CCC" style={{ marginBottom: '16px' }} />
            <h3 style={{ color: '#333', margin: '0 0 8px 0' }}>No Orders This Week</h3>
            <p style={{ color: '#888', margin: 0 }}>Try navigating to a different week.</p>
          </EmptyState>
        ) : (
          <>
            <TopActions className="no-print">
              <PrintButton onClick={() => window.print()} style={{ margin: 0 }}>
                <Printer size={16} /> Print Orders
              </PrintButton>
            </TopActions>

          <OrdersGrid>
              {orders.map((order, idx) => (
                <OrderCard key={order.id || idx}>
                  <OrderHeader>
                    <OrderMeta>
                      <InfoRow>
                        <MapPin size={18} /> <span style={{ fontSize: '18px', fontWeight: 800 }}>{order.branchName}</span>
                      </InfoRow>
                    </OrderMeta>
                  </OrderHeader>

                  {renderOrderDetails(order)}
                </OrderCard>
              ))}
            </OrdersGrid>

            {/* 프린트 전용: 매장 × 요일별 1페이지 레이아웃 */}
            {orders.map((order, idx) => {
              const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
              return days.map(dayName => {
                const dayData = weekDays.find(d => d.dayName === dayName);
                const items = order.dailyItems?.[dayName] || [];
                if (items.length === 0) return null;
                return (
                  <PrintBranchCard key={`print-${order.id || idx}-${dayName}`}>
                    <PrintBranchHeader>
                      <PrintBranchName>{order.branchName}</PrintBranchName>
                      <PrintWeekLabel>Week of {weekDays[0].shortDate} – {weekDays[6].shortDate}</PrintWeekLabel>
                    </PrintBranchHeader>
                    <PrintDayHeader>{dayName}  ·  {dayData?.shortDate}</PrintDayHeader>
                    {items.map((item: OrderItem) => (
                      <PrintItemLine key={item.id}>
                        <PrintCheckboxPrint />
                        <PrintQty>{item.quantity}×</PrintQty>
                        <PrintItemName>{item.name}</PrintItemName>
                      </PrintItemLine>
                    ))}
                  </PrintBranchCard>
                );
              });
            })}
          </>
        )}
        <div className="no-print" style={{ textAlign: 'center', width: '100%', marginTop: '40px', paddingBottom: '80px' }}>
          {orders.length > 0 && (
            <PrintButton onClick={() => window.print()}>
              <Printer size={16} /> Print Orders
            </PrintButton>
          )}
          <LogoutButton onClick={handleLogout}>{lang.list.logout}</LogoutButton>
        </div>
      </ContentWrapper>
      <ScrollToTopButton className="no-print" $visible={showScrollTop} onClick={scrollToTop} aria-label="Scroll to top">
        <ArrowUp size={20} />
      </ScrollToTopButton>

      <style>{`
        @media print {
          body {
            margin: 0;
            padding: 0;
          }
          @page {
            size: A4;
            margin: 10mm 12mm;
          }
        }
      `}</style>
    </PageContainer>
  );
};

export default DeliveryList;
